import React from 'react';
import { Heart, Compass, Briefcase, Users, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

// Header
const Header = () => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
            4uventures
          </div>
          <nav className="hidden md:flex items-center space-x-8">
            {['Home', 'About', 'Services', 'Team', 'Contact'].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item.toLowerCase())}
                className="text-slate-700 hover:text-slate-900 font-medium transition-colors"
              >
                {item}
              </button>
            ))}
            <Button onClick={() => scrollToSection('contact')} className="bg-slate-900 hover:bg-slate-800">
              Get Started
            </Button>
          </nav>
        </div>
      </div>
    </header>
  );
};

// Hero Section
const Hero = () => (
  <section id="home" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
    <div className="absolute inset-0 z-0">
      <img 
        src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920"
        alt="Business skyline"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-800/90 to-slate-900/80"></div>
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="max-w-3xl">
        <div className="inline-block mb-6">
          <span className="bg-slate-700/50 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium">
            Professional Business Ventures
          </span>
        </div>
        
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
          Building Tomorrow's Success Stories
        </h1>
        
        <p className="text-xl md:text-2xl text-slate-200 mb-10 leading-relaxed max-w-2xl">
          A forward-thinking venture firm dedicated to identifying opportunities, fostering growth, and creating sustainable value across diverse industries.
        </p>

        <div className="flex flex-col sm:flex-row gap-4">
          <Button 
            size="lg" 
            onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all"
          >
            Learn More
          </Button>
          <Button 
            size="lg" 
            variant="outline"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg font-semibold transition-all"
          >
            Contact Us
          </Button>
        </div>
      </div>
    </div>
  </section>
);

// Services Section
const Services = () => {
  const services = [
    { icon: Compass, title: 'Strategic Consulting', description: 'Expert guidance for business growth, market expansion, and competitive positioning' },
    { icon: Users, title: 'Business Development', description: 'Comprehensive solutions to scale operations and maximize revenue potential' },
    { icon: Briefcase, title: 'Investment Advisory', description: 'Strategic investment analysis, portfolio management, and risk optimization' },
  ];

  return (
    <section id="services" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Services</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive solutions designed to drive sustainable growth and deliver measurable results for your business
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <Card key={index} className="border-2 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group">
                <CardHeader>
                  <div className="w-16 h-16 bg-slate-900 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="text-white" size={32} />
                  </div>
                  <CardTitle className="text-2xl">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-lg leading-relaxed">{service.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// About Section
const About = () => (
  <section id="about" className="py-24 bg-slate-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">About 4uventures</h2>
          <p className="text-lg text-gray-700 mb-6 leading-relaxed">
            We are a forward-thinking venture firm committed to driving sustainable growth and creating lasting value for our partners and clients.
          </p>
          <p className="text-lg text-gray-700 mb-6 leading-relaxed">
            With a focus on strategic excellence and innovative solutions, we work across diverse industries to identify opportunities, solve complex challenges, and deliver measurable results.
          </p>
          <div className="grid grid-cols-2 gap-6 mt-8">
            <div className="text-center p-6 bg-white rounded-lg shadow-md">
              <div className="text-3xl font-bold text-slate-900 mb-2">25+</div>
              <div className="text-sm text-gray-600 font-medium">Years Combined Experience</div>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-md">
              <div className="text-3xl font-bold text-slate-900 mb-2">100%</div>
              <div className="text-sm text-gray-600 font-medium">Client Commitment</div>
            </div>
          </div>
        </div>
        <div className="relative h-96 rounded-lg overflow-hidden shadow-2xl">
          <img 
            src="https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=800"
            alt="Professional office environment"
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    </div>
  </section>
);

// Team Section
const Team = () => {
  const team = [
    { name: 'Joseph Lee', role: 'Founder & CEO', image: 'https://static.prod-images.emergentagent.com/jobs/04488b04-3f31-4007-a33d-b6e79834d404/images/8cbba0af4b7196b37b10f3b151276163f8b838f3e08d2933b40dbdf4e26bcf24.jpeg' },
    { name: 'Marcus Thompson', role: 'Manager', image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400' },
    { name: 'Ebony Shackelford', role: 'Chief Financial Officer', image: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=400' },
  ];

  return (
    <section id="team" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Meet Our Leadership</h2>
          <p className="text-xl text-gray-600">Experienced professionals dedicated to your success</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {team.map((member, index) => (
            <Card key={index} className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group">
              <div className="relative h-[500px] overflow-hidden bg-slate-100">
                <img 
                  src={member.image} 
                  alt={member.name} 
                  className="w-full h-full object-contain object-center group-hover:scale-105 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <CardContent className="p-6 bg-slate-50">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{member.name}</h3>
                <p className="text-slate-600 font-semibold text-lg">{member.role}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const Contact = () => (
  <section id="contact" className="py-24 bg-slate-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Get In Touch</h2>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">Let's discuss how we can help your business achieve sustainable growth and lasting success</p>
      </div>

      <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
        <Card className="text-center border-2 hover:border-slate-900 transition-all hover:shadow-xl">
          <CardContent className="p-8">
            <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <Phone className="text-white" size={28} />
            </div>
            <h3 className="font-bold text-lg mb-2">Phone</h3>
            <p className="text-gray-600 font-medium">313-314-1809</p>
          </CardContent>
        </Card>

        <Card className="text-center border-2 hover:border-slate-900 transition-all hover:shadow-xl">
          <CardContent className="p-8">
            <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="text-white" size={28} />
            </div>
            <h3 className="font-bold text-lg mb-2">Email</h3>
            <p className="text-gray-600 font-medium break-all">4universalventures.com</p>
          </CardContent>
        </Card>

        <Card className="text-center border-2 hover:border-slate-900 transition-all hover:shadow-xl">
          <CardContent className="p-8">
            <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="text-white" size={28} />
            </div>
            <h3 className="font-bold text-lg mb-2">Location</h3>
            <p className="text-gray-600 font-medium">Flint, MI</p>
          </CardContent>
        </Card>
      </div>

      <div className="mt-16 text-center">
        <div className="inline-block bg-white p-8 rounded-xl shadow-xl max-w-2xl">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Get Started?</h3>
          <p className="text-gray-600 mb-6">
            Whether you're seeking strategic consulting, business development, or investment advisory services, we're here to help you succeed.
          </p>
          <a href="tel:313-314-1809">
            <Button size="lg" className="bg-slate-900 hover:bg-slate-800 px-8 py-6 text-lg font-semibold">
              Schedule a Consultation
            </Button>
          </a>
        </div>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="bg-slate-900 text-white py-16">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-3 gap-12 mb-8">
        <div>
          <h3 className="text-2xl font-bold mb-4">4uventures</h3>
          <p className="text-slate-400 leading-relaxed">
            Building sustainable success through strategic vision, innovative solutions, and unwavering commitment to excellence.
          </p>
        </div>
        
        <div>
          <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
          <ul className="space-y-2">
            <li><a href="#home" className="text-slate-400 hover:text-white transition-colors">Home</a></li>
            <li><a href="#about" className="text-slate-400 hover:text-white transition-colors">About</a></li>
            <li><a href="#services" className="text-slate-400 hover:text-white transition-colors">Services</a></li>
            <li><a href="#team" className="text-slate-400 hover:text-white transition-colors">Team</a></li>
            <li><a href="#contact" className="text-slate-400 hover:text-white transition-colors">Contact</a></li>
          </ul>
        </div>

        <div>
          <h4 className="text-lg font-semibold mb-4">Contact Information</h4>
          <ul className="space-y-3 text-slate-400">
            <li className="flex items-start gap-2">
              <Phone size={18} className="mt-1 flex-shrink-0" />
              <span>313-314-1809</span>
            </li>
            <li className="flex items-start gap-2">
              <Mail size={18} className="mt-1 flex-shrink-0" />
              <span>4universalventures.com</span>
            </li>
            <li className="flex items-start gap-2">
              <MapPin size={18} className="mt-1 flex-shrink-0" />
              <span>Flint, MI</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800 pt-8 text-center">
        <p className="text-slate-400">© {new Date().getFullYear()} 4uventures LLC. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

// Main Component
export const FourUVentures = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <Hero />
      <About />
      <Services />
      <Team />
      <Contact />
      <Footer />
    </div>
  );
};

export default FourUVentures;
